import { useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

const RECAPTCHA_SITE_KEY = '6LfTuNIrAAAAAORnXjSKwG828siNQxyxi23q81qN'; // v3 site key

export default function Contact() {
  const [params] = useSearchParams();
  const sent = params.get('sent') === '1';
  const captchaFailed = params.get('captcha') === '0'; // ← moved inside component

  const formRef = useRef(null);
  const [captchaError, setCaptchaError] = useState('');

  // Load reCAPTCHA v3 script once
  useEffect(() => {
    if (window.grecaptcha?.execute) return;
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    s.async = true;
    s.defer = true;
    document.body.appendChild(s);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // With noValidate on the form, call reportValidity() to show messages
    if (!formRef.current.reportValidity()) return;

    try {
      // Wait for grecaptcha to be ready
      await new Promise((res) => {
        if (window.grecaptcha?.ready) return window.grecaptcha.ready(res);
        const i = setInterval(() => {
          if (window.grecaptcha?.ready) { clearInterval(i); window.grecaptcha.ready(res); }
        }, 50);
      });

      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact' });
      if (!token) throw new Error('No token');

      // Attach token and submit
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'recaptcha_token';
      hidden.value = token;
      formRef.current.appendChild(hidden);

      setCaptchaError('');
      formRef.current.submit();
    } catch {
      setCaptchaError('reCAPTCHA failed to load. Please try again.');
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
      <h1 className="text-4xl font-bold">Contact Us</h1>

      {captchaFailed && (
        <div className="mb-4 rounded-xl bg-red-50 text-red-700 p-3">
          Please retry — reCAPTCHA validation failed.
        </div>
      )}

      {sent ? (
        <div className="mb-4 mt-4 rounded-xl bg-green-50 text-green-800 p-3">
          Thanks — we&apos;ve got your message and we&apos;ll get back to you as soon as possible.
        </div>
      ) : (
        <div>
          <p className="mt-6 text-lg text-slate-700 dark:text-slate-300">
            Book a demo today and join our growing list of satisfied customers. Whether you have questions about our product, or need support we&apos;re here to help.
          </p>

          <form
            ref={formRef}
            className="mt-8 glass rounded-2xl p-6 grid gap-4"
            name="contact"
            method="POST"
            action="/api/contact"
            onSubmit={handleSubmit}
            noValidate
          >
            {/* honeypot */}
            <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" />

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1" htmlFor="name">Name</label>
                <input
                  id="name"
                  name="name"
                  required
                  minLength={2}
                  className="w-full px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-300 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <div>
                <label className="block text-sm mb-1" htmlFor="email">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-300 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm mb-1" htmlFor="company">Company</label>
              <input
                id="company"
                name="company"
                className="w-full px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-300 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>

            <div>
              <label className="block text-sm mb-1" htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                required
                minLength={10}
                rows={5}
                className="w-full px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-300 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Min 10 characters.</p>
            </div>

            {captchaError && (
              <p className="text-sm text-red-600" role="alert">{captchaError}</p>
            )}

            <div className="flex gap-3 mt-2">
              <button type="submit" className="px-5 py-3 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-semibold">
                Send
              </button>
              <a href="mailto:hello@cranelogix.com" className="px-5 py-3 rounded-xl glass font-semibold">
                Or email us
              </a>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
